<template>
<div class="hris-page">
  <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">System Settings</span>
      <h1 class="hris-page-title">Tenant Enrollment</h1>
      <p class="hris-page-subtitle">Manage tenants, their branding, and account status.</p>
    </div>

    <div class="row q-col-gutter-lg">
      <div class="col-12 col-md-4">
        <div class="hris-form-section">
          <h2 class="hris-form-section-title">Tenant Details</h2>
          <q-form @submit.prevent="saveTenant">
            <div class="hris-form-row">
              <div class="hris-form-field">
                <q-input
                  outlined
                  dense
                  color="green"
                  v-model="tenantForm.name"
                  label="Tenant Name"
                  :rules="[formRules.required]"
                ></q-input>
              </div>
            </div>
            <div class="hris-form-row">
              <div class="hris-form-field">
                <q-input
                  outlined
                  dense
                  color="green"
                  v-model="tenantForm.slug"
                  label="Slug"
                  hint="lowercase hyphenated"
                ></q-input>
              </div>
            </div>
            <div class="hris-form-row">
              <div class="hris-form-field">
                <q-input outlined dense color="green" v-model="tenantForm.initials" label="Initials"></q-input>
              </div>
            </div>
            <div class="hris-form-row">
              <div class="hris-form-field">
                <q-select
                  outlined
                  dense
                  color="green"
                  v-model="tenantForm.is_active"
                  :options="statusOptions"
                  label="Status"
                  emit-value
                  map-options
                ></q-select>
              </div>
            </div>
            <div class="hris-form-row">
              <div class="hris-form-field">
                <q-file
                  :key="logoInputKey"
                  label="Logo (optional)"
                  outlined
                  dense
                  color="green"
                  counter
                  accept="image/*"
                  :max-file-size="maxLogoSize"
                  @change="handleLogoChange"
                ></q-file>
              </div>
            </div>
            <div class="hris-form-actions" style="justify-content: stretch;">
              <q-btn
                class="hris-q-primary full-width"
                unelevated
                label="Save Tenant"
                :loading="savingTenant"
                type="submit"
              ></q-btn>
              <q-btn
                class="hris-q-outline full-width"
                unelevated
                label="Reset"
                :disable="savingTenant"
                @click.prevent="resetTenantForm"
              ></q-btn>
            </div>
          </q-form>
        </div>

        <div class="hris-panel" style="margin-top: 20px;">
          <div class="row items-center justify-between">
            <h2 class="hris-panel-title" style="margin-bottom: 0;">Actions</h2>
            <div class="row items-center q-gutter-sm">
              <q-btn
                dense
                round
                flat
                class="hris-q-ghost"
                aria-label="Refresh tenant list"
                @click="loadTenants"
                :loading="loadingTenants"
              >
                <i class="fas fa-sync-alt"></i>
                <q-tooltip anchor="top middle" self="bottom middle">Refresh Tenants</q-tooltip>
              </q-btn>
              <q-btn
                dense
                round
                flat
                class="hris-q-ghost"
                aria-label="Clear tenant form"
                @click.prevent="resetTenantForm"
              >
                <i class="fas fa-eraser"></i>
                <q-tooltip anchor="top middle" self="bottom middle">Clear Form</q-tooltip>
              </q-btn>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-8">
        <div class="hris-panel">
          <div class="row items-center justify-between q-mb-md">
            <h2 class="hris-panel-title" style="margin-bottom: 0;">Tenant Directory</h2>
            <q-btn dense round flat class="hris-q-ghost" @click.prevent="resetTenantForm" aria-label="Add new tenant">
              <i class="fas fa-plus"></i>
              <q-tooltip anchor="top middle" self="bottom middle">New Tenant</q-tooltip>
            </q-btn>
          </div>

          <q-table
            flat
            :data="tenants"
            :columns="columns"
            row-key="id"
            :pagination.sync="pagination"
            :loading="loadingTenants"
            @row-click="editTenant"
          >
            <template v-slot:body="props">
              <q-tr :props="props" style="cursor: pointer;">
                <q-td key="logo" :props="props">
                  <div class="tenant-logo-wrapper">
                    <img
                      v-if="props.row.logo_path"
                      :src="logoUrl(props.row.logo_path)"
                      :alt="props.row.name"
                    />
                    <span v-else class="text-grey">—</span>
                  </div>
                </q-td>
                <q-td key="name" :props="props">{{ props.row.name }}</q-td>
                <q-td key="slug" :props="props">{{ props.row.slug }}</q-td>
                <q-td key="initials" :props="props">{{ props.row.initials || '—' }}</q-td>
                <q-td key="status" :props="props">
                  <span class="hris-badge" :class="props.row.is_active === 'Y' ? 'hris-badge-success' : 'hris-badge-danger'">
                    {{ props.row.is_active === 'Y' ? 'Active' : 'Inactive' }}
                  </span>
                </q-td>
                <q-td key="storage" :props="props">
                  <span class="storage-path">{{ props.row.storage_path || '—' }}</span>
                </q-td>
                <q-td key="actions" :props="props">
                  <div class="row items-center justify-center q-gutter-sm">
                    <q-btn
                      round
                      dense
                      flat
                      class="hris-q-ghost"
                      aria-label="Edit tenant"
                      @click.stop="editTenant(props.row)"
                    >
                      <i class="fas fa-pen"></i>
                      <q-tooltip anchor="top middle" self="bottom middle">Edit Tenant</q-tooltip>
                    </q-btn>
                    <q-btn
                      round
                      dense
                      flat
                      class="hris-q-ghost"
                      aria-label="Delete tenant"
                      @click.stop="confirmDeleteTenant(props.row)"
                    >
                      <i class="fas fa-trash"></i>
                      <q-tooltip anchor="top middle" self="bottom middle">Delete Tenant</q-tooltip>
                    </q-btn>
                  </div>
                </q-td>
              </q-tr>
            </template>
            <template v-slot:no-data>
              <div class="hris-empty full-width">No tenants found.</div>
            </template>
          </q-table>
        </div>
      </div>
    </div>

    <q-dialog v-model="deleteModal" persistent>
      <div class="hris-modal" style="max-width: 420px;">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Delete Tenant</h2>
        </div>
        <div class="hris-modal-body">
          Are you sure you want to delete <strong>{{ tenantToDelete?.name || '' }}</strong>?
        </div>
        <div class="hris-modal-footer">
          <q-btn flat label="Cancel" class="hris-q-ghost" :disable="deletingTenant" @click="closeDeleteModal"></q-btn>
          <q-btn unelevated class="hris-q-primary" :loading="deletingTenant" label="Delete" @click="deleteTenant"></q-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';

export default {
  mixins: [mixins],
  data() {
    return {
      tenants: [],
      loadingTenants: false,
      deleteModal: false,
      tenantToDelete: null,
      savingTenant: false,
      deletingTenant: false,
      tenantForm: {
        tempID: 0,
        name: '',
        slug: '',
        initials: '',
        is_active: 'Y'
      },
      tenantLogoFile: null,
      logoInputKey: 0,
      maxLogoSize: 5 * 1024 * 1024,
      pagination: {
        rowsPerPage: 10
      },
      statusOptions: [
        { label: 'Active', value: 'Y' },
        { label: 'Inactive', value: 'N' }
      ],
      columns: [
        { name: 'logo', align: 'center', label: '', field: row => row.logo_path, sortable: false },
        { name: 'name', align: 'left', label: 'Tenant', field: 'name', sortable: true },
        { name: 'slug', align: 'left', label: 'Slug', field: 'slug', sortable: true },
        { name: 'initials', align: 'center', label: 'Initials', field: 'initials', sortable: true },
        { name: 'status', align: 'center', label: 'Status', field: 'is_active', sortable: true },
        { name: 'storage', align: 'left', label: 'Storage Path', field: 'storage_path', sortable: false },
        { name: 'actions', align: 'center', label: 'Actions', field: 'actions', sortable: false }
      ]
    };
  },
  created() {
    this.loadTenants();
  },
  methods: {
    loadTenants() {
      this.loadingTenants = true;
      return axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` }
        })
        .get('/admin/tenants')
        .then((res) => {
          this.tenants = res.data.data || [];
        })
        .finally(() => {
          this.loadingTenants = false;
        });
    },
    editTenant(tenant) {
      if (tenant) {
        this.tenantForm = {
          tempID: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          initials: tenant.initials || '',
          is_active: tenant.is_active || 'Y'
        };
      }
    },
    resetTenantForm() {
      this.tenantForm = {
        tempID: 0,
        name: '',
        slug: '',
        initials: '',
        is_active: 'Y'
      };
      this.tenantLogoFile = null;
      this.logoInputKey += 1;
    },
    handleLogoChange(value) {
      if (!value) {
        this.tenantLogoFile = null;
        return;
      }
      const selected = Array.isArray(value) ? value[0] : value;
      if (selected && selected.size > this.maxLogoSize) {
        this.$q.notify({ color: 'negative', message: 'Logo must be 5 MB or smaller.' });
        this.tenantLogoFile = null;
        this.logoInputKey += 1;
        return;
      }
      this.tenantLogoFile = selected;
    },
    saveTenant() {
      if (!this.tenantForm.name) {
        this.$q.notify({ color: 'negative', message: 'Tenant name is required.' });
        return;
      }
      this.savingTenant = true;
      const formData = new FormData();
      formData.append('tempID', this.tenantForm.tempID);
      formData.append('name', this.tenantForm.name);
      formData.append('slug', this.tenantForm.slug);
      formData.append('initials', this.tenantForm.initials);
      formData.append('is_active', this.tenantForm.is_active);
      if (this.tenantLogoFile) {
        formData.append('logo', this.tenantLogoFile, this.tenantLogoFile.name);
      }

      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` }
        })
        .post('/admin/tenant', formData)
        .then((res) => {
          this.$q.notify({ color: 'positive', message: res.data.message || 'Tenant saved.' });
          this.loadTenants();
          this.resetTenantForm();
        })
        .catch((error) => {
          const message = (error.response && error.response.data && error.response.data.message) || 'Unable to save tenant.';
          this.$q.notify({ color: 'negative', message });
        })
        .finally(() => {
          this.savingTenant = false;
        });
    },
    confirmDeleteTenant(tenant) {
      this.tenantToDelete = tenant;
      this.deleteModal = true;
    },
    closeDeleteModal() {
      this.deleteModal = false;
      this.tenantToDelete = null;
    },
    deleteTenant() {
      if (!this.tenantToDelete) {
        return;
      }
      this.deletingTenant = true;
      const formData = new FormData();
      formData.append('tempID', this.tenantToDelete.id);
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` }
        })
        .post('/admin/tenant/delete', formData)
        .then((res) => {
          this.$q.notify({ color: 'positive', message: res.data.message || 'Tenant removed.' });
          this.loadTenants();
        })
        .catch((error) => {
          const message = (error.response && error.response.data && error.response.data.message) || 'Unable to remove tenant.';
          this.$q.notify({ color: 'negative', message });
        })
        .finally(() => {
          this.deletingTenant = false;
          this.closeDeleteModal();
        });
    },
    logoUrl(path) {
      if (!path) {
        return '';
      }
      const normalized = path.replace(/^\//, '');
      return `${this.apiUrl}/${normalized}`;
    }
  }
};
</script>

<style scoped>
.tenant-logo-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.tenant-logo-wrapper img {
  max-width: 80px;
  max-height: 40px;
  object-fit: contain;
}

.storage-path {
  font-size: 0.9rem;
  word-break: break-all;
}
</style>
